import React from "react";
import TagButton from "../common/TagButton";
import { useScreen } from "../../context/ScreenContext";

const folderIconColor = ["#C8D0DC", "#97A0B0", "#B0BACD"];
const fullData = [
  {
    title: "Pitch Perfect",
    description:
      "A global financial services organization with a diverse portfolio across banking, transactions, and asset management.",
    businessNeeds:
      "Client relationship teams often manage complex, high-value customer interactions. Maintaining consistency and personalized service is challenging, especially during staff transitions or when onboarding new team members. There is also a pressing need to streamline workload while ensuring every client interaction is insightful and productive.",
    ourSolution:
      "Developed an AI-powered voice agent, trained on extensive historical data including recorded conversations, summaries, notes, and performance insights. The AI supports relationship teams by enhancing preparation, improving engagement quality, and offering coaching to accelerate onboarding and upskilling for new team members.",
    expectedOutcomes: [
      "Significant reduction in onboarding and training time.",
      "Increased team efficiency and improved return on client engagement efforts.",
      "Strengthened compliance and quality assurance across interactions",
    ],
    color: "#D8DDF2",
  },
  {
    title: "ExecView AI",
    description:
      "A global financial services organization with a broad portfolio spanning banking, transactions, and asset management.",
    businessNeeds:
      "Employees across the organization sought better access to leadership insights and perspectives to inform decision-making and foster learning. Traditional engagement methods were limited by time zones, availability, and access restrictions.",
    ourSolution:
      "Developed a secure AI-powered leadership insights agent, enabling employees to access curated viewpoints on industry trends, organizational leadership, diversity topics, and personal development areas. The solution supports multilingual interactions and ensures content access is appropriately scoped and compliant.",
    expectedOutcomes: [
      "Enhanced employee engagement and continuous learning through AI-facilitated leadership insights.",
      "24/7 global accessibility while maintaining security and topic boundaries.",
    ],
    color: "#C8D0DC",
  },
  {
    title: "Atom AI",
    description:
      "An AI product company focused on empowering IT teams with agentic service management to automate workflows and drive business growth, building the future of enterprise service management.",
    businessNeeds:
      "HR and IT teams struggled with handling user onboarding, access requests, and service management efficiently. Employees faced delays in resolving IT issues due to fragmented support channels and manual troubleshooting. A seamless, AI-driven solution was required to enhance productivity and streamline workflows.",
    ourSolution:
      "We built a voice-enabled browser agent that lets users resolve IT and HR queries via natural language. It captures web-page context in real time and searches enterprise knowledge sources like wikis and service catalogs to deliver accurate answers. With voice commands, employees can troubleshoot, request access, and get hands-free guidance for faster resolution.",
    expectedOutcomes: [
      "Achieved 65% request deflection rate, reducing reliance on human agents through effective self-service.",
      "Enabled secure, seamless integration with internal knowledge platforms like Confluence, SharePoint, Google Drive, and Notion.",
    ],
    color: "#97A0B0",
  },
  {
    title: "Design Consult AI Assistant",
    description:
      "A specialty furniture brand focused on delivering customized interior solutions with an emphasis on safety, style, and personalization.",
    businessNeeds:
      "The manual consultation process for customized interior projects was time-consuming, often taking several weeks due to multiple discussions and follow-ups between clients and designers. The brand sought a faster, more efficient method to streamline consultations and accelerate design finalization without compromising personalization.",
    ourSolution:
      "Developed InspireAI, an AI-powered design consultation assistant that automates the initial client discovery process. Through natural conversations, the assistant gathers essential information about preferences, lifestyle factors, and design themes. It then synthesizes this data into a structured visual summary and generates initial concepts and mood boards, significantly shortening the design cycle.",
    expectedOutcomes: [
      "Reduced consultation time from weeks to days, enabling faster project initiation.",
      "Improved designer productivity and enhanced customer experience by minimizing administrative overhead and maximizing creative focus.",
    ],
    color: "#B0BACD",
  },
];

const ProofInProjects = () => {
  const { isMobile } = useScreen();
  const dataToRender = isMobile ? fullData.slice(2, 4) : fullData;
  const [index, setIndex] = React.useState(0);
  let visibleTabIdx = 0;

  return (
    <div className="flex flex-col gap-[36px] pb-[25px] md:py-[25px] px-[20px] md:px-[120px] mx-auto pt-[36px]">
      <TagButton text="Case Studies" />
      <div
        className="bg-clip-text text-transparent text-[24px] md:text-[40px] font-semibold leading-[53.24px] font-montserrat inline-block w-fit"
        style={{
          backgroundImage: "linear-gradient(90deg, #000 0%, #0060C7 100%)",
        }}
      >
        Proof in Projects
      </div>

      <div className="relative">
        <div className="flex">
          <div className="group rounded-tl-[8px] overflow-hidden hover:cursor-pointer mb-[-8px] z-100">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width={isMobile ? "152px" : "186"}
              height={isMobile ? "36" : "44"}
              viewBox="0 0 186 44"
              fill="none"
            >
              <path
                d="M0 0.5H148.073C153.348 0.5 158.283 3.09934 161.267 7.44856L186 43.5H0V0.5Z"
                fill="#D8DDF2"
              />
              <foreignObject x="0" y="0" width="186" height="44">
                <div
                  xmlns="http://www.w3.org/1999/xhtml"
                  className="text-[#000] text-center font-montserrat text-[16px] font-medium leading-[45.543px] tracking-[-0.678px] group-hover:underline"
                >
                  Case Study {index + 1}
                </div>
              </foreignObject>
            </svg>
          </div>
          {dataToRender.map((data, idx) => {
            if (idx === index) return null;
            const color = folderIconColor[visibleTabIdx];
            visibleTabIdx++;
            return (
              <div
                className="group rounded-tl-[8px] overflow-hidden hover:cursor-pointer mb-[-8px] transform transition-all hover:-translate-y-[4px] ml-[-40px]"
                style={{ zIndex: 10 - idx * 2 }}
                key={idx}
                onClick={() => setIndex(idx)}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width={isMobile ? "152px" : "186"}
                  height={isMobile ? "36" : "44"}
                  viewBox="0 0 186 44"
                  fill="none"
                >
                  <path
                    d="M0 0.5H148.073C153.348 0.5 158.283 3.09934 161.267 7.44856L186 43.5H0V0.5Z"
                    fill={color}
                  />
                  <foreignObject x="0" y="0" width="186" height="44">
                    <div
                      xmlns="http://www.w3.org/1999/xhtml"
                      className="text-[#000] text-center font-montserrat text-[16px] font-medium leading-[45.543px] tracking-[-0.678px] group-hover:underline"
                    >
                      Case Study {idx + 1}
                    </div>
                  </foreignObject>
                </svg>
              </div>
            );
          })}
        </div>

        <div className="flex flex-col gap-[16px] z-500 relative items-center bg-[#D8DDF2] p-[16px] md:p-[35px] rounded-[8px] rounded-tl-[0px] shadow-md max-w-[1200px]">
          <div className="flex flex-col md:flex-row justify-evenly gap-[40px]">
            <div className="flex flex-col gap-[32px] items-start md:w-[33%] text-justify md:text-start">
              <p className="text-black text-center font-montserrat text-[32px] not-italic font-medium leading-[45.543px] tracking-[-0.678px]">
                {dataToRender[index].title}
              </p>
              <p className="text-[#444] font-roboto text-[16px] not-italic font-light leading-[22px] md:text-justify">
                {dataToRender[index].description}
              </p>
            </div>

            <div className="w-[1px] bg-[#727272] h-[250px] hidden md:block"></div>

            <div className="text-[#444] font-roboto text-[16px] not-italic font-normal leading-[22px] flex flex-col gap-[13px] text-justify md:text-start md:w-[33%]">
              <p className="flex max-w-max px-[6.387px] py-[5.921px] pt-[5.921px] pr-[6.387px] pb-[6.767px] pl-[6.767px] justify-center items-center gap-0 rounded-[5.075px] bg-[#FFEBEB]">
                <span className="text-[#FF6B6B] font-roboto text-[10.151px] font-medium leading-[10.151px]">
                  Business Needs
                </span>
              </p>
              <p className="text-[#444] font-roboto text-base not-italic font-light leading-[22px] text-start md:text-justify">
                {dataToRender[index].businessNeeds}
              </p>
            </div>

            <div className="w-[1px] bg-[#727272] h-[250px] hidden md:block"></div>

            <div className="text-[#444] text-justify font-roboto text-[16px] not-italic font-normal leading-[22px] flex flex-col gap-[13px] md:w-[33%]">
              <p className="flex max-w-max px-[6.387px] py-[5.921px] pt-[5.921px] pr-[6.387px] pb-[6.767px] pl-[6.767px] justify-center items-center gap-0 rounded-[5.075px] bg-[#EBF8DE]">
                <span className="text-[#68A168] font-inter text-[10.517px] font-medium leading-[10.517px]">
                  Our Solutions
                </span>
              </p>
              <p className="text-[#444] font-roboto text-base not-italic font-light leading-[22px] text-start md:text-justify">
                {dataToRender[index].ourSolution}
              </p>
            </div>
          </div>

          <div className="w-full bg-[#727272] h-[1px] hidden md:block"></div>

          <div className="flex flex-col gap-[30px] justify-items-start w-full">
            <div className="rounded-[5.358px] bg-[rgba(122,215,255,0.15)] inline-flex px-[6.412px] py-[6.251px] pt-[6.251px] pb-[7.144px] pl-[7.144px] pr-[6.412px] justify-center items-center gap-[-0.589px] max-w-max">
              <span className="text-[#4899CB] font-inter text-[10.717px] font-medium leading-[10.717px]">
                Expected Outcomes
              </span>
            </div>
            <ul className="list-disc text-[#222] font-roboto text-[16px] font-normal leading-[22px] md:leading-[16px] pl-[20px] space-y-2">
              {dataToRender[index].expectedOutcomes.map((outcome, idx) => (
                <li
                  key={idx}
                  className="text-[#222] font-roboto text-base not-italic font-light leading-[22px] md:leading-[16px] text-justify md:text-start"
                >
                  {outcome}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProofInProjects;
